// SampleHeaderCard.tsx
import { MetaField, MetaGrid } from '@/components/DetailPage';
import QCBadge from '@/components/UI/QCBadge';
import { useDateFormatter } from '@/hooks';
import detailHeaderStyles from '@/styles/components/details.page.module.css';
import type { SampleWithNamesPublic } from '@/types';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { toQCStatus } from './qcStatus';
import styles from './SampleHeaderCard.module.css';

export interface SampleHeaderCardProps {
  sample: SampleWithNamesPublic;
}

function SampleHeaderCardComponent({ sample }: SampleHeaderCardProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const empty = t('empty', { ns: 'general' });

  // QC mapping is a best guess; Controls QC has no backing field.
  // TODO(backend): confirm canonical QC set + label↔field mapping; add controls_qc.
  const qcCells: { label: string; status: ReturnType<typeof toQCStatus> }[] = [
    { label: t('samples.details.qcSampleQuality'), status: toQCStatus(sample.image_qc) },
    { label: t('samples.details.qcDecode'), status: toQCStatus(sample.decode_qc) },
    { label: t('samples.details.qcLane'), status: toQCStatus(sample.run_qc) },
    { label: t('samples.details.qcControls'), status: 'N/A' },
  ];

  return (
    <Stack className={clsx(styles.card, detailHeaderStyles.header)} gap={4} orientation="vertical">
      <Stack alignItems="start" fullWidth justifyContent="space-between" orientation="horizontal">
        <Text as="h1" size="xl">
          {sample.name}
        </Text>
        {sample.assay_version !== null && sample.assay_version !== undefined && sample.assay_version !== '' && (
          <Text as="span" size="lg">
            {sample.assay_version}
          </Text>
        )}
      </Stack>

      <Grid fullWidth gap={6} gridTemplateColumns="2fr 1fr">
        <MetaGrid columns={2}>
          <MetaField label={t('samples.details.fieldSpecies')} placeholder={empty} value={sample.species} />
          <MetaField label={t('samples.details.fieldSampleGroup')} placeholder={empty} value={sample.sample_group} />
          <MetaField
            label={t('samples.details.fieldModified')}
            placeholder={empty}
            value={
              sample.updated_at !== null && sample.updated_at !== undefined ? formatDate(sample.updated_at) : undefined
            }
          />
          <MetaField label={t('samples.details.fieldCreated')} value={formatDate(sample.created_at)} />
          <MetaField
            label={t('samples.details.fieldStudy')}
            value={
              <Link params={{ studyId: sample.study_id }} to="/studies/$studyId">
                {sample.study_name}
              </Link>
            }
          />
          <MetaField
            label={t('samples.details.fieldOrganisation')}
            value={
              <Link params={{ orgId: sample.org_id }} to="/organizations/$orgId">
                {sample.org_name}
              </Link>
            }
          />
        </MetaGrid>

        <Stack gap={4} orientation="vertical">
          <Grid gap={3} gridTemplateColumns="repeat(4, 1fr)">
            {qcCells.map(cell => (
              <Stack className={styles.qcCell} key={cell.label}>
                <Text as="span" className={styles.qcLabel} size="xs">
                  {cell.label}
                </Text>
                <QCBadge qc={cell.status} />
              </Stack>
            ))}
          </Grid>

          <Stack className={styles.messageBox} gap={1} orientation="vertical">
            <Text as="span" size="xs">
              {t('samples.details.messageHeading')}
            </Text>
            <Text as="span" size="sm">
              {sample.message !== null && sample.message !== undefined && sample.message !== ''
                ? sample.message
                : t('samples.details.messageEmpty')}
            </Text>
          </Stack>
        </Stack>
      </Grid>
    </Stack>
  );
}

export const SampleHeaderCard = memo(SampleHeaderCardComponent);
